import {
  Component,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
  HostListener,
} from "@angular/core";
import { ActivatedRoute, Router, RouterStateSnapshot } from "@angular/router";
import { Subscription, combineLatest } from "rxjs";
import { DeleteDpParams } from "src/app/store/actions/global-subheader.actions";
import { TAB_TYPE } from "../constants";
import { Store } from "@ngrx/store";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import { AppService } from "src/app/app.service";
import { ComparativeAnalysisService } from "./../comparative-analysis.service";
import { NzModalRef, NzModalService } from "ng-zorro-antd/modal";
import { CommonActionModalComponent } from "../common-action-modal/common-action-modal.component";
import { MARKET_DATA_LIST } from "src/app/common-constants";
import { CreateNewComparativeAnalysisWizardComponent } from "../create-new-comparative-analysis-wizard/create-new-comparative-analysis-wizard.component";
import {
  createJsonForSaveAsFeature,
  createJsonForCreateNewFeature,
} from "../comparative-analysis-utils";
import { UpdateComparativeAnalysisData } from "src/app/store/actions/global.actions";
import { SetComparativeHeaderTitle } from "src/app/store/actions/comparative-analysis.actions";
import { ShareCompsTableComponent } from "src/app/comps-page/share-comps-table/share-comps-table.component";
@Component({
  selector: "app-comparative-analysis",
  templateUrl: "./comparative-analysis.component.html",
  styleUrls: ["./comparative-analysis.component.scss"],
  host: { class: "comparative-analysis-container" },
  encapsulation: ViewEncapsulation.None,
})
export class ComparativeAnalysisComponent implements OnInit, OnDestroy {
  pageType: "current" | "user";
  tabList = TAB_TYPE;
  tabType;
  compType;
  compId;
  selectedTabIndex = TAB_TYPE.trend.index;
  routeParams;
  routeQueryParams;
  initializedTabs = [true];
  industryId;
  trendMetaResponse;
  trendCompState = undefined;
  valuationState;
  chartsTrendState;
  chartsComparisonState;
  chartsRevisionState;
  tprState;
  revisionState;
  surpriseState;
  shouldFetchTrendData = false;
  shouldFetchComparisionData = false;
  shouldFetchValuationData = false;
  shouldFetchChartsData = false;
  shouldFetchTprData = false;
  shouldFetchRevisionData = false;
  shouldFetchSurpriseData = false;
  recentlyVisitedCompanies = [];
  watchlistData;
  industryMenuData;
  marketGeoData;
  suggestedPeers;
  hasUnsavedData;
  actionModal: NzModalRef;
  createNewTrendsModalTitle: string = "New Analysis Table";
  createNewTrendsModal: NzModalRef;
  maxTickers = 500;
  maxLineitems = 20;
  stdLineItems: Array<any> = [];
  periodsWithAvailableDataForPopup;
  suggestedLineItemMenuName = "Suggested Line Items";
  trendCompLineItemsData;
  promptModal: NzModalRef = null;
  shareCompsTableModal: NzModalRef;
  nextStateUrl: string = "";
  downloadInProgress: boolean = false;
  sortByColumnId;

  //urls
  legacyUrl = `${window.caUrl}/app`;

  //subscribers
  routeParamSubscriber: Subscription;
  WatchlistSubscriber: Subscription;
  industryMenuSubscriber: Subscription;
  marketGeoSubscriber: Subscription;
  lineItemsSubscriber: Subscription;
  createActionPopupSubscriber: Subscription;
  createNewModalSubscriber: Subscription;
  globalAppSettingSubscriber: Subscription;
  recentlyVisitedCompaniesSubscription: Subscription;
  getUserCompIdSubscription: Subscription;
  getCompanyPeersSubscription: Subscription;
  saveUnsavedCompsSubscription: Subscription;
  getStdLinesSubscription: Subscription;
  getPeriodsWithDataSubscription: Subscription;
  updateCompsNameSubscription: Subscription;
  duplicateCustomCompsSubscription: Subscription;
  deleteCustomCompsSubscription: Subscription;

  //loaders
  isWatchlistMenuApiInProgress;
  isIndustryMenuApiInProgress;
  isMarketGeoApiInProgress;
  suggestedPeersApiInProgress;
  recentlyVisitedCompanyInProgress;
  isStdLineItemsUpdateInProgress;

  // modals
  isShareTableModalVisible = false;

  @HostListener("window:beforeunload", ["$event"])
  unloadNotification($event: any) {
    if (this.hasUnsavedData && !this.downloadInProgress) {
      $event.returnValue = true;
    }
    this.downloadInProgress = false;
  }

  constructor(
    private route: ActivatedRoute,
    private store: Store<AppState>,
    private appService: AppService,
    private modalService: NzModalService,
    public router: Router,
    private dataService: ComparativeAnalysisService,
  ) {}

  ngOnInit(): void {
    const urlParametrs = combineLatest(
      this.route.params,
      this.route.queryParams,
      (params, queryParams) => ({
        params,
        queryParams,
      }),
    );

    this.createActionPopupSubscriber =
      this.appService.onLeftPanelTemplateAction.subscribe((actionTypeObj) => {
        if (actionTypeObj["action_type"] === "share") {
          this.shareComparativeAnalysis(actionTypeObj);
        } else {
          this.createActionModal(actionTypeObj);
        }
      });

    this.createNewModalSubscriber =
      this.appService.onGlobalSubheaderAction.subscribe(() => {
        this.createNewModal();
      });

    this.routeParamSubscriber = urlParametrs.subscribe((item) => {
      if (this.selectedTabIndex !== 0) {
        this.selectedTabIndex = 0;
      }
      const { params, queryParams } = item;
      this.routeParams = params;
      this.routeQueryParams = queryParams;
      this.pageType = params.pageType;
      this.compType = this.pageType === "user" ? "user" : queryParams.comp_type;
      this.compId = this.pageType === "user" ? params.compId : undefined;

      if (this.pageType === "user" && !this.compId) {
        const urlParams = this.route.queryParams["value"];
        // shared analysis case
        if (urlParams && urlParams["shared_id"]) {
          this.getUserCompIdSubscription = this.dataService
            .getUserCompId(urlParams)
            .subscribe((res) => {
              if (res && res.data) {
                if (res.data["comp_type"] && res.data["comp_type"] === "user") {
                  this.compId = res.data["tab_id"];
                  this.router
                    .navigate([`/comparative-analysis/user/${this.compId}`])
                    .then(() => {
                      this.appService.updateLeftPanelSource.next({
                        currentComparativeData: null,
                      });
                      this.dataService.updateLeftPanel(false);
                    });
                  if (!res.data["exists"]) {
                    this.store.dispatch(
                      new UpdateComparativeAnalysisData({
                        tabId: this.compId,
                        label: res.data.comps_name,
                        operation: "add",
                      }),
                    );
                  }
                }
              }
            });
        } else {
          // random url ending with: comparative-analysis/user
          const param = {};
          this.getUserCompIdSubscription = this.dataService
            .getUserCompId(param)
            .subscribe((res) => {
              if (res && res.data) {
                let linkToNavigate = "";
                let params = {};
                if (
                  res.data["comp_type"] &&
                  res.data["comp_type"] === "user_comp"
                ) {
                  linkToNavigate = `/comparative-analysis/user/${res.data["tab_id"]}`;
                } else {
                  linkToNavigate = `/comparative-analysis/current`;
                  params = {
                    comp_type: "industry",
                    id: res.data["tab_id"],
                    sub_id: -1,
                  };
                }
                this.router
                  .navigate([linkToNavigate], { queryParams: params })
                  .then(() => {
                    this.dataService.updateLeftPanel(false);
                  });
              }
            });
        }
      }
      if (this.pageType === "current") {
        this.valuationState = {};
        this.chartsTrendState = {};
        this.chartsComparisonState = {};
        this.chartsRevisionState = {};
        this.tprState = {};
        this.revisionState = {};
        this.surpriseState = {};
      }
      if (TAB_TYPE[this.tabType]) {
        this.selectedTabIndex = TAB_TYPE[this.tabType].index;
      }

      if (this.compType === "industry") {
        this.industryId = parseInt(queryParams.id) || 166;
      }
    });

    this.globalAppSettingSubscriber = this.store
      .select(
        stateGetter("globalHeader", "globalAppSettings", "period_settings"),
      )
      .subscribe((data) => {
        if (data) {
          // shouldFetchData initiates data calls for respective tabs
          // each tab has their own subscriber, so when tab is active, the subscriber handles the data calls,  this also prevent the data call when tab is inactive
          // enabling shouldFetchData for inactive tabs, so that when user visits that tab, it will initisate the data calls
          if (this.selectedTabIndex === 0) {
            this.shouldFetchComparisionData = true;
            this.shouldFetchValuationData = true;
            this.shouldFetchTprData = true;
            this.shouldFetchChartsData = true;
            this.shouldFetchRevisionData = true;
            this.shouldFetchSurpriseData = true;
          } else if (this.selectedTabIndex === 1) {
            this.shouldFetchTrendData = true;
            this.shouldFetchValuationData = true;
            this.shouldFetchTprData = true;
            this.shouldFetchChartsData = true;
            this.shouldFetchRevisionData = true;
            this.shouldFetchSurpriseData = true;
          } else if (this.selectedTabIndex === 2) {
            this.shouldFetchTrendData = true;
            this.shouldFetchComparisionData = true;
            this.shouldFetchValuationData = true;
            this.shouldFetchTprData = true;
            this.shouldFetchChartsData = true;
            this.shouldFetchSurpriseData = true;
          } else if (this.selectedTabIndex === 3) {
            this.shouldFetchTrendData = true;
            this.shouldFetchComparisionData = true;
            this.shouldFetchValuationData = true;
            this.shouldFetchTprData = true;
            this.shouldFetchChartsData = true;
            this.shouldFetchRevisionData = true;
          } else if (this.selectedTabIndex === 4) {
            this.shouldFetchTrendData = true;
            this.shouldFetchComparisionData = true;
            this.shouldFetchTprData = true;
            this.shouldFetchChartsData = true;
            this.shouldFetchRevisionData = true;
            this.shouldFetchSurpriseData = true;
          } else if (this.selectedTabIndex === 5) {
            this.shouldFetchTrendData = true;
            this.shouldFetchComparisionData = true;
            this.shouldFetchValuationData = true;
            this.shouldFetchChartsData = true;
            this.shouldFetchRevisionData = true;
            this.shouldFetchSurpriseData = true;
          } else if (this.selectedTabIndex === 6) {
            this.shouldFetchTrendData = true;
            this.shouldFetchComparisionData = true;
            this.shouldFetchValuationData = true;
            this.shouldFetchTprData = true;
            this.shouldFetchRevisionData = true;
            this.shouldFetchSurpriseData = true;
          }
        }
      });

    setTimeout(() => {
      this.fetchTickerModalData();
    }, 300);
  }

  ngOnDestroy(): void {
    if (this.createActionPopupSubscriber) {
      this.createActionPopupSubscriber.unsubscribe();
    }

    if (this.createNewModalSubscriber) {
      this.createNewModalSubscriber.unsubscribe();
    }

    if (this.routeParamSubscriber) {
      this.routeParamSubscriber.unsubscribe();
    }

    if (this.globalAppSettingSubscriber) {
      this.globalAppSettingSubscriber.unsubscribe();
    }

    if (this.WatchlistSubscriber) {
      this.WatchlistSubscriber.unsubscribe();
    }

    if (this.industryMenuSubscriber) {
      this.industryMenuSubscriber.unsubscribe();
    }

    if (this.marketGeoSubscriber) {
      this.marketGeoSubscriber.unsubscribe();
    }

    if (this.lineItemsSubscriber) {
      this.lineItemsSubscriber.unsubscribe();
    }

    if (this.recentlyVisitedCompaniesSubscription) {
      this.recentlyVisitedCompaniesSubscription.unsubscribe();
    }
    if (this.getUserCompIdSubscription) {
      this.getUserCompIdSubscription.unsubscribe();
    }
    if (this.getCompanyPeersSubscription) {
      this.getCompanyPeersSubscription.unsubscribe();
    }
    if (this.saveUnsavedCompsSubscription) {
      this.saveUnsavedCompsSubscription.unsubscribe();
    }
    if (this.getStdLinesSubscription) {
      this.getStdLinesSubscription.unsubscribe();
    }
    if (this.getPeriodsWithDataSubscription) {
      this.getPeriodsWithDataSubscription.unsubscribe();
    }
    if (this.updateCompsNameSubscription) {
      this.updateCompsNameSubscription.unsubscribe();
    }
    if (this.duplicateCustomCompsSubscription) {
      this.duplicateCustomCompsSubscription.unsubscribe();
    }
    if (this.deleteCustomCompsSubscription) {
      this.deleteCustomCompsSubscription.unsubscribe();
    }
  }

  handleTabChange(index) {
    if (!this.initializedTabs[index]) {
      this.initializedTabs[index] = true;
    }
    this.store.dispatch(new DeleteDpParams());
  }

  updateGridSortByColumnId() {
    this.sortByColumnId = "";
  }

  updateTrendCompState(data) {
    this.trendCompState = {
      ...this.trendCompState,
      ...data,
    };
    this.shouldFetchTrendData = true;
    this.shouldFetchComparisionData = true;
    if (data.tickers) {
      this.valuationState = {
        ...this.valuationState,
        tickers: data.tickers,
      };
      this.shouldFetchValuationData = true;

      this.tprState = {
        ...this.tprState,
        tickers: data.tickers,
      };
      this.shouldFetchTprData = true;

      this.revisionState = {
        ...this.revisionState,
        tickers: data.tickers,
      };
      this.shouldFetchRevisionData = true;

      this.surpriseState = {
        ...this.surpriseState,
        tickers: data.tickers,
      };
      this.shouldFetchSurpriseData = true;

      this.chartsTrendState = {
        ...this.chartsTrendState,
        tickers: data.tickers,
        customTickers: [],
        tickerSelectionType: "top_ten",
      };
      this.chartsComparisonState = {
        ...this.chartsTrendState,
        tickers: data.tickers,
        customTickers: [],
        tickerSelectionType: "top_ten",
      };
      this.chartsRevisionState = {
        ...this.chartsTrendState,
        tickers: data.tickers,
        customTickers: [],
        tickerSelectionType: "top_ten",
      };
      this.shouldFetchChartsData = true;

      this.updateSuggestedPeers(data.tickers);
    }
    if (data.currency) {
      this.valuationState = {
        ...this.valuationState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchValuationData = true;

      this.revisionState = {
        ...this.revisionState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchRevisionData = true;

      this.surpriseState = {
        ...this.surpriseState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchSurpriseData = true;

      this.tprState = {
        ...this.tprState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchTprData = true;
    }
    if (data.periodData) {
      this.chartsComparisonState = {
        ...this.chartsComparisonState,
        periodData: data.periodData,
        periodList: data.periodList,
        periodUserSetting: data.periodUserSetting,
        periodType: data.periodType,
        periodLabel: data.periodLabel,
      };
      this.shouldFetchChartsData = true;
    }
  }

  updateTrendCompStateWithoutMakingDataCalls(data) {
    this.trendCompState = {
      ...this.trendCompState,
      ...data,
    };
  }

  updateValuationState(data) {
    this.valuationState = {
      ...this.valuationState,
      ...data,
    };
    this.shouldFetchValuationData = true;
    if (data.tickers) {
      this.trendCompState = {
        ...this.trendCompState,
        tickers: data.tickers,
      };
      this.shouldFetchTrendData =
        data.loadingWithMeta && this.initializedTabs[0] ? false : true;
      this.shouldFetchComparisionData =
        data.loadingWithMeta && this.initializedTabs[1] ? false : true;

      this.revisionState = {
        ...this.revisionState,
        tickers: data.tickers,
      };
      this.shouldFetchRevisionData =
        data.loadingWithMeta && this.initializedTabs[2] ? false : true;

      this.surpriseState = {
        ...this.surpriseState,
        tickers: data.tickers,
      };
      this.shouldFetchSurpriseData =
        data.loadingWithMeta && this.initializedTabs[3] ? false : true;

      this.tprState = {
        ...this.tprState,
        tickers: data.tickers,
      };
      this.shouldFetchTprData =
        data.loadingWithMeta && this.initializedTabs[5] ? false : true;

      this.chartsTrendState = {
        ...this.chartsTrendState,
        tickers: data.tickers,
        customTickers: [],
        tickerSelectionType: "top_ten",
      };
      this.chartsComparisonState = {
        ...this.chartsComparisonState,
        tickers: data.tickers,
        customTickers: [],
        tickerSelectionType: "top_ten",
      };
      this.chartsRevisionState = {
        ...this.chartsRevisionState,
        tickers: data.tickers,
        customTickers: [],
        tickerSelectionType: "top_ten",
      };
      this.shouldFetchChartsData =
        data.loadingWithMeta && this.initializedTabs[6] ? false : true;

      this.updateSuggestedPeers(data.tickers);
    }
    if (data.currency) {
      this.trendCompState = {
        ...this.trendCompState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchTrendData =
        data.loadingWithMeta && this.initializedTabs[0] ? false : true;
      this.shouldFetchComparisionData =
        data.loadingWithMeta && this.initializedTabs[1] ? false : true;

      this.revisionState = {
        ...this.revisionState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchRevisionData =
        data.loadingWithMeta && this.initializedTabs[2] ? false : true;

      this.surpriseState = {
        ...this.surpriseState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchSurpriseData =
        data.loadingWithMeta && this.initializedTabs[3] ? false : true;

      this.tprState = {
        ...this.tprState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchTprData =
        data.loadingWithMeta && this.initializedTabs[5] ? false : true;
    }
  }

  updateTprState(data) {
    this.tprState = {
      ...this.tprState,
      ...data,
    };
    this.shouldFetchTprData = true;
    if (data.tickers) {
      this.trendCompState = {
        ...this.trendCompState,
        tickers: data.tickers,
      };
      this.shouldFetchTrendData =
        data.loadingWithMeta && this.initializedTabs[0] ? false : true;
      this.shouldFetchComparisionData =
        data.loadingWithMeta && this.initializedTabs[1] ? false : true;

      this.revisionState = {
        ...this.revisionState,
        tickers: data.tickers,
      };
      this.shouldFetchRevisionData =
        data.loadingWithMeta && this.initializedTabs[2] ? false : true;

      this.surpriseState = {
        ...this.surpriseState,
        tickers: data.tickers,
      };
      this.shouldFetchSurpriseData =
        data.loadingWithMeta && this.initializedTabs[3] ? false : true;

      this.valuationState = {
        ...this.valuationState,
        tickers: data.tickers,
      };
      this.shouldFetchValuationData =
        data.loadingWithMeta && this.initializedTabs[4] ? false : true;

      this.chartsTrendState = {
        ...this.chartsTrendState,
        tickers: data.tickers,
        customTickers: [],
        tickerSelectionType: "top_ten",
      };
      this.chartsComparisonState = {
        ...this.chartsComparisonState,
        tickers: data.tickers,
        customTickers: [],
        tickerSelectionType: "top_ten",
      };
      this.chartsRevisionState = {
        ...this.chartsRevisionState,
        tickers: data.tickers,
        customTickers: [],
        tickerSelectionType: "top_ten",
      };
      this.shouldFetchChartsData =
        data.loadingWithMeta && this.initializedTabs[6] ? false : true;

      this.updateSuggestedPeers(data.tickers);
    }
    if (data.currency) {
      this.trendCompState = {
        ...this.trendCompState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchTrendData =
        data.loadingWithMeta && this.initializedTabs[0] ? false : true;
      this.shouldFetchComparisionData =
        data.loadingWithMeta && this.initializedTabs[1] ? false : true;

      this.revisionState = {
        ...this.revisionState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchRevisionData =
        data.loadingWithMeta && this.initializedTabs[2] ? false : true;

      this.surpriseState = {
        ...this.surpriseState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchSurpriseData =
        data.loadingWithMeta && this.initializedTabs[3] ? false : true;

      this.valuationState = {
        ...this.valuationState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchValuationData =
        data.loadingWithMeta && this.initializedTabs[4] ? false : true;
    }
  }

  updateChartState(params) {
    const { chartStateToUpdate, data } = params;
    if (chartStateToUpdate.includes("trend")) {
      this.updateChartTrendState(data);
    }
    if (chartStateToUpdate.includes("comparison")) {
      this.updateChartComparisonState(data);
    }
    if (chartStateToUpdate.includes("revision")) {
      this.updateChartRevisionState(data);
    }
    if (data.tickers) {
      this.trendCompState = {
        ...this.trendCompState,
        tickers: data.tickers,
      };
      this.shouldFetchTrendData =
        data.loadingWithMeta && this.initializedTabs[0] ? false : true;
      this.shouldFetchComparisionData =
        data.loadingWithMeta && this.initializedTabs[1] ? false : true;

      this.revisionState = {
        ...this.revisionState,
        tickers: data.tickers,
      };
      this.shouldFetchRevisionData =
        data.loadingWithMeta && this.initializedTabs[2] ? false : true;

      this.surpriseState = {
        ...this.surpriseState,
        tickers: data.tickers,
      };
      this.shouldFetchSurpriseData =
        data.loadingWithMeta && this.initializedTabs[3] ? false : true;

      this.valuationState = {
        ...this.valuationState,
        tickers: data.tickers,
      };
      this.shouldFetchValuationData =
        data.loadingWithMeta && this.initializedTabs[4] ? false : true;

      this.tprState = {
        ...this.tprState,
        tickers: data.tickers,
      };
      this.shouldFetchTprData =
        data.loadingWithMeta && this.initializedTabs[5] ? false : true;

      this.updateSuggestedPeers(data.tickers);
    }
  }

  updateChartTrendState(data) {
    this.chartsTrendState = {
      ...this.chartsTrendState,
      ...data,
    };
    this.shouldFetchChartsData = true;
  }

  updateChartComparisonState(data) {
    this.chartsComparisonState = {
      ...this.chartsComparisonState,
      ...data,
    };
    this.shouldFetchChartsData = true;

    if (data.periodData) {
      this.trendCompState = {
        ...this.trendCompState,
        periodData: data.periodData,
        periodList: data.periodList,
        periodUserSetting: data.periodUserSetting,
        periodType: data.periodType,
        periodLabel: data.periodLabel,
      };
      this.shouldFetchTrendData =
        data.loadingWithMeta && this.initializedTabs[0] ? false : true;
      this.shouldFetchComparisionData =
        data.loadingWithMeta && this.initializedTabs[1] ? false : true;
    }
  }

  updateChartRevisionState(data) {
    this.chartsRevisionState = {
      ...this.chartsRevisionState,
      ...data,
    };
    this.shouldFetchChartsData = true;
  }

  updateRevisionState(data) {
    this.revisionState = {
      ...this.revisionState,
      ...data,
    };
    this.shouldFetchRevisionData = true;
    if (data.tickers) {
      this.trendCompState = {
        ...this.trendCompState,
        tickers: data.tickers,
      };
      this.shouldFetchTrendData =
        data.loadingWithMeta && this.initializedTabs[0] ? false : true;
      this.shouldFetchComparisionData =
        data.loadingWithMeta && this.initializedTabs[1] ? false : true;

      this.valuationState = {
        ...this.valuationState,
        tickers: data.tickers,
      };
      this.shouldFetchValuationData =
        data.loadingWithMeta && this.initializedTabs[4] ? false : true;

      this.surpriseState = {
        ...this.surpriseState,
        tickers: data.tickers,
      };
      this.shouldFetchSurpriseData =
        data.loadingWithMeta && this.initializedTabs[3] ? false : true;

      this.tprState = {
        ...this.tprState,
        tickers: data.tickers,
      };
      this.shouldFetchTprData =
        data.loadingWithMeta && this.initializedTabs[5] ? false : true;

      this.chartsTrendState = {
        ...this.chartsTrendState,
        tickers: data.tickers,
        customTickers: [],
        tickerSelectionType: "top_ten",
      };
      this.chartsComparisonState = {
        ...this.chartsComparisonState,
        tickers: data.tickers,
        customTickers: [],
        tickerSelectionType: "top_ten",
      };
      this.chartsRevisionState = {
        ...this.chartsRevisionState,
        tickers: data.tickers,
        customTickers: [],
        tickerSelectionType: "top_ten",
      };
      this.shouldFetchChartsData =
        data.loadingWithMeta && this.initializedTabs[6] ? false : true;

      this.updateSuggestedPeers(data.tickers);
    }
    if (data.currency) {
      this.trendCompState = {
        ...this.trendCompState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchTrendData =
        data.loadingWithMeta && this.initializedTabs[0] ? false : true;
      this.shouldFetchComparisionData =
        data.loadingWithMeta && this.initializedTabs[1] ? false : true;

      this.valuationState = {
        ...this.valuationState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchValuationData =
        data.loadingWithMeta && this.initializedTabs[4] ? false : true;

      this.surpriseState = {
        ...this.surpriseState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchSurpriseData =
        data.loadingWithMeta && this.initializedTabs[3] ? false : true;

      this.tprState = {
        ...this.tprState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchTprData =
        data.loadingWithMeta && this.initializedTabs[5] ? false : true;
    }
  }

  updateSurpriseState(data) {
    this.surpriseState = {
      ...this.surpriseState,
      ...data,
    };
    this.shouldFetchSurpriseData = true;
    if (data.tickers) {
      this.trendCompState = {
        ...this.trendCompState,
        tickers: data.tickers,
      };
      this.shouldFetchTrendData =
        data.loadingWithMeta && this.initializedTabs[0] ? false : true;
      this.shouldFetchComparisionData =
        data.loadingWithMeta && this.initializedTabs[1] ? false : true;

      this.valuationState = {
        ...this.valuationState,
        tickers: data.tickers,
      };
      this.shouldFetchValuationData =
        data.loadingWithMeta && this.initializedTabs[4] ? false : true;

      this.revisionState = {
        ...this.revisionState,
        tickers: data.tickers,
      };
      this.shouldFetchRevisionData =
        data.loadingWithMeta && this.initializedTabs[2] ? false : true;

      this.tprState = {
        ...this.tprState,
        tickers: data.tickers,
      };
      this.shouldFetchTprData =
        data.loadingWithMeta && this.initializedTabs[5] ? false : true;

      this.chartsTrendState = {
        ...this.chartsTrendState,
        tickers: data.tickers,
        customTickers: [],
        tickerSelectionType: "top_ten",
      };
      this.chartsComparisonState = {
        ...this.chartsComparisonState,
        tickers: data.tickers,
        customTickers: [],
        tickerSelectionType: "top_ten",
      };
      this.chartsRevisionState = {
        ...this.chartsRevisionState,
        tickers: data.tickers,
        customTickers: [],
        tickerSelectionType: "top_ten",
      };
      this.shouldFetchChartsData =
        data.loadingWithMeta && this.initializedTabs[6] ? false : true;

      this.updateSuggestedPeers(data.tickers);
    }

    if (data.currency) {
      this.trendCompState = {
        ...this.trendCompState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchTrendData =
        data.loadingWithMeta && this.initializedTabs[0] ? false : true;
      this.shouldFetchComparisionData =
        data.loadingWithMeta && this.initializedTabs[1] ? false : true;

      this.valuationState = {
        ...this.valuationState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchValuationData =
        data.loadingWithMeta && this.initializedTabs[4] ? false : true;

      this.revisionState = {
        ...this.revisionState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchRevisionData =
        data.loadingWithMeta && this.initializedTabs[2] ? false : true;

      this.tprState = {
        ...this.tprState,
        currency: data.currency,
        magnitudeMessage: data.magnitudeMessage,
      };
      this.shouldFetchTprData =
        data.loadingWithMeta && this.initializedTabs[5] ? false : true;
    }
  }

  onTrendDataFetchInitiate() {
    this.shouldFetchTrendData = false;
  }

  onComparisionDataFetchInitiate() {
    this.shouldFetchComparisionData = false;
  }

  onValuationDataFetchInitiate() {
    this.shouldFetchValuationData = false;
  }

  onTprDataFetchInitiate() {
    this.shouldFetchTprData = false;
  }

  onChartDataFetchInitiate() {
    this.shouldFetchChartsData = false;
  }

  onRevisionDataFetchInitiate() {
    this.shouldFetchRevisionData = false;
  }

  onSurpriseDataFetchInitiate() {
    this.shouldFetchSurpriseData = false;
  }

  fetchTickerModalData() {
    this.getRecentVisitedTickers();
    this.getwatchlistData();
    this.getIndustryMenuData();
    this.getMarketGeoData();
  }

  getRecentVisitedTickers() {
    this.recentlyVisitedCompanyInProgress = true;
    this.recentlyVisitedCompaniesSubscription = this.dataService
      .getRecentlyVisitedCompanies()
      .subscribe((res) => {
        // this.store.dispatch(new SetDisableActions(false));

        if (res && res.status === "success" && res.data) {
          const recentViewCompanies = res.data.map((el) => {
            const company = {
              full_name: el.name,
              name: el.name,
              ticker: el.ticker,
              sli_comps_id: 0,
              mpt: el.mpt,
              isSelected: false,
              cid: el.cid,
            };
            return company;
          });

          this.recentlyVisitedCompanies = recentViewCompanies;
        }
        this.recentlyVisitedCompanyInProgress = false;
      });
  }

  getwatchlistData() {
    this.isWatchlistMenuApiInProgress = true;
    this.WatchlistSubscriber = this.dataService
      .fetchComparativeAnalysisMenuData("watchlist")
      .subscribe((response) => {
        if (
          response.status === "success" &&
          response.data &&
          response.data.watchlist
        ) {
          this.watchlistData = [...response.data.watchlist];
        }
        this.isWatchlistMenuApiInProgress = false;
      });
  }

  getIndustryMenuData() {
    this.isIndustryMenuApiInProgress = true;
    this.industryMenuSubscriber = this.dataService
      .fetchComparativeAnalysisMenuData("industry")
      .subscribe((response) => {
        if (
          response.status === "success" &&
          response.data &&
          response.data.industry
        ) {
          this.industryMenuData = [...response.data.industry];
        }
        this.isIndustryMenuApiInProgress = false;
      });
  }

  getMarketGeoData() {
    this.isMarketGeoApiInProgress = false;
    this.marketGeoSubscriber = this.dataService
      .fetchMarketAndGeoData()
      .subscribe((response: any) => {
        if (response.status === "success" && response.data) {
          this.marketGeoData = response.data;
        }
        this.isMarketGeoApiInProgress = false;
      });
  }

  updateSuggestedPeers(tickers) {
    this.suggestedPeersApiInProgress = true;
    const rowTickers = tickers.map((item) => {
      return item.ticker;
    });

    if (rowTickers && rowTickers.length) {
      const totalTickerLength = rowTickers.length || 0;
      const firstTenTickers = rowTickers.slice(0, 10);
      const firstTenTickersLength = firstTenTickers.length || 0;
      const limit = totalTickerLength - firstTenTickersLength + 10;

      this.getCompanyPeersSubscription = this.dataService
        .getCompanyPeers(firstTenTickers, null, limit)
        .subscribe((res) => {
          if (res && res.status === "success") {
            const responsePeers =
              (res.peer_info || []).reduce(function (result, item) {
                const checkIfSuggestedTickerAlreadyExists = rowTickers.includes(
                  item.ticker,
                );
                if (!checkIfSuggestedTickerAlreadyExists) {
                  const updatedTicker = { ...item, full_name: item.name };
                  result.push(updatedTicker);
                }
                return result;
              }, []) || [];

            this.suggestedPeers = responsePeers.slice(0, 10);
          }

          const suggestedPeerCompanies = this.suggestedPeers.map((pc) => {
            const company = {
              full_name: pc.name,
              name: pc.name,
              ticker: pc.ticker,
              sli_comps_id: 0,
              mpt: pc.mpt,
              isSelected: false,
              cid: pc.cid,
            };
            return company;
          });

          this.suggestedPeers = suggestedPeerCompanies;

          this.suggestedPeersApiInProgress = false;
        });
    }
  }

  updateUnsavedDataStatus() {
    this.hasUnsavedData = true;
  }

  updateDownloadStatus(status) {
    this.downloadInProgress = status;
  }

  createActionModal(actionDetailObj) {
    this.actionModal = this.modalService.create({
      nzWrapClassName: "vertical-center-modal",
      nzContent: CommonActionModalComponent,
      nzFooter: null,
      nzClosable: false,
      nzWidth: 542,
      nzBodyStyle: { padding: "16px 24px", height: "306px" },
      nzComponentParams: {
        actionDetailObj: actionDetailObj,
        onSaveAsOrDeleteClick: (data) =>
          this.handleCompSaveAsOrDeleteClick(data),
        onExitWithoutSavingClick: () => this.handleExitWithoutSavingClick(),
      },
      nzStyle: {},
    });
  }

  handleCompSaveAsOrDeleteClick(data) {
    if (data.actionButton === "save") {
      this.handleSaveAsClick(data);
    } else if (data.actionButton === "delete") {
      this.handleDeleteClick(data);
    } else if (data.actionButton === "duplicate") {
      this.handleDuplicateClick(data);
    } else if (data.actionButton === "rename") {
      this.handleRenameClick(data);
    } else if (data.actionButton === "exit_without_saving") {
      this.handleSaveBeforeExit(data);
    }
  }

  handleExitWithoutSavingClick() {
    this.actionModal.destroy();
    this.hasUnsavedData = false;
    this.router.navigate([this.nextStateUrl]);
  }

  handleSaveBeforeExit(data) {
    this.hasUnsavedData = false;
    const updatedTemplateName = data.nameField.nativeElement.value;
    const payload = createJsonForSaveAsFeature(
      updatedTemplateName,
      this.trendCompState,
      this.trendCompState,
      this.valuationState,
      this.chartsTrendState,
      this.chartsComparisonState,
      this.chartsRevisionState,
      this.tprState,
      this.revisionState,
      this.surpriseState,
    );
    this.saveUnsavedCompsSubscription = this.dataService
      .saveUnsavedComps(payload)
      .subscribe((response) => {
        if (response.status === "duplicate_error") {
          data.isDuplicateName = true;
        }
        if (response.status === "success") {
          this.actionModal.destroy();
          data.isDuplicateName = false;
          this.router.navigate([this.nextStateUrl]).then(() => {
            this.appService.updateLeftPanelSource.next({
              currentComparativeData: null,
            });
            this.dataService.updateLeftPanel(false);
          });
          this.store.dispatch(
            new UpdateComparativeAnalysisData({
              tabId: response.tab_id,
              label: updatedTemplateName,
              operation: "add",
            }),
          );
        }
      });
  }

  handleSaveAsClick(data) {
    const updatedTemplateName = data.nameField.nativeElement.value;
    const payload = createJsonForSaveAsFeature(
      updatedTemplateName,
      this.trendCompState,
      this.trendCompState,
      this.valuationState,
      this.chartsTrendState,
      this.chartsComparisonState,
      this.chartsRevisionState,
      this.tprState,
      this.revisionState,
      this.surpriseState,
    );
    this.saveUnsavedCompsSubscription = this.dataService
      .saveUnsavedComps(payload)
      .subscribe((response) => {
        this.hasUnsavedData = false;
        if (response.status === "duplicate_error") {
          data.isDuplicateName = true;
        }
        if (response.status === "success") {
          this.actionModal.destroy();
          data.isDuplicateName = false;
          this.router
            .navigate([`/comparative-analysis/user/${response.tab_id}`])
            .then(() => {
              this.appService.updateLeftPanelSource.next({
                currentComparativeData: null,
              });
              this.dataService.updateLeftPanel(false);
            });
          this.store.dispatch(
            new UpdateComparativeAnalysisData({
              tabId: response.tab_id,
              label: updatedTemplateName,
              operation: "add",
            }),
          );
        }
      });
  }

  handleDeleteClick(data) {
    const payload = {
      cc_id: data.templateId,
    };
    this.deleteCustomCompsSubscription = this.dataService
      .deleteCustomComps(payload)
      .subscribe((response) => {
        if (response.status === "success") {
          this.actionModal.destroy();
          const param = {};
          this.getUserCompIdSubscription = this.dataService
            .getUserCompId(param)
            .subscribe((res) => {
              if (res && res.data) {
                let linkToNavigate = "";
                let params = {};
                if (
                  res.data["comp_type"] &&
                  res.data["comp_type"] === "user_comp"
                ) {
                  linkToNavigate = `/comparative-analysis/user/${res.data["tab_id"]}`;
                } else {
                  linkToNavigate = `/comparative-analysis/current`;
                  params = {
                    comp_type: "industry",
                    id: res.data["tab_id"],
                    sub_id: -1,
                  };
                }
                this.router
                  .navigate([linkToNavigate], { queryParams: params })
                  .then(() => {
                    this.dataService.updateLeftPanel(false);
                    this.store.dispatch(
                      new UpdateComparativeAnalysisData({
                        tabId: data.templateId,
                        operation: "delete",
                      }),
                    );
                  });
              } else {
                this.router
                  .navigate([`/comparative-analysis/user`])
                  .then(() => {
                    this.store.dispatch(
                      new UpdateComparativeAnalysisData({
                        tabId: data.templateId,
                        operation: "delete",
                      }),
                    );
                  });
              }
            });
        }
      });
  }

  handleDuplicateClick(data) {
    const updatedTemplateName = data.nameField.nativeElement.value;
    const payload = {
      cc_id: data.templateId,
      comps_name: updatedTemplateName,
    };
    this.duplicateCustomCompsSubscription = this.dataService
      .duplicateCustomComps(payload)
      .subscribe((response) => {
        if (response.status === "duplicate_error") {
          data.isDuplicateName = true;
        }
        if (response.status === "success") {
          this.actionModal.destroy();
          data.isDuplicateName = false;
          this.router
            .navigate([`/comparative-analysis/user/${response.tab_id}`])
            .then(() => {
              this.dataService.updateLeftPanel(false);
              this.store.dispatch(
                new UpdateComparativeAnalysisData({
                  tabId: response.tab_id,
                  label: updatedTemplateName,
                  operation: "add",
                }),
              );
            });
        }
      });
  }

  handleRenameClick(data) {
    const updatedTemplateName = data.nameField.nativeElement.value;
    const payload = {
      cc_id: data.templateId,
      comps_name: updatedTemplateName,
    };
    this.updateCompsNameSubscription = this.dataService
      .updateCompsName(payload)
      .subscribe((response) => {
        if (response.status === "duplicate_error") {
          data.isDuplicateName = true;
        }
        if (response.status === "success") {
          this.actionModal.destroy();
          data.isDuplicateName = false;
          this.dataService.updateLeftPanel(false);
          this.store.dispatch(
            new SetComparativeHeaderTitle(updatedTemplateName),
          );
          if (this.trendCompState) {
            this.trendCompState.headerTitle = updatedTemplateName;
          }
          if (this.valuationState) {
            this.valuationState.headerTitle = updatedTemplateName;
          }
          if (this.chartsComparisonState) {
            this.chartsComparisonState.headerTitle = updatedTemplateName;
          }
          if (this.chartsRevisionState) {
            this.chartsRevisionState.headerTitle = updatedTemplateName;
          }
          if (this.chartsTrendState) {
            this.chartsTrendState.headerTitle = updatedTemplateName;
          }
          this.store.dispatch(
            new UpdateComparativeAnalysisData({
              tabId: data.templateId,
              label: updatedTemplateName,
              operation: "update",
            }),
          );
        }
      });
  }

  shareComparativeAnalysis(actionTypeObj) {
    this.isShareTableModalVisible = true;
    this.shareCompsTableModal = this.modalService.create({
      nzTitle: "Share Comparative Analysis",
      nzContent: ShareCompsTableComponent,
      nzMaskClosable: false,
      nzComponentParams: {
        tabId: parseInt(actionTypeObj["template_id"]),
        componentType: "comparative-analysis",
        is_cc: true,
      },
      nzWrapClassName: "comps-modal-container vertical-center-modal",
      nzFooter: null,
      nzOnCancel: (): void => {
        this.isShareTableModalVisible = false;
      },
    });
  }

  createNewModal() {
    this.createNewTrendsModal = this.modalService.create({
      nzTitle: this.createNewTrendsModalTitle,
      nzContent: CreateNewComparativeAnalysisWizardComponent,
      nzMaskClosable: false,
      nzComponentParams: {
        addRowTableData: [],
        // suggestedPeers,
        recentlyVisitedCompanies: {
          company_info: this.recentlyVisitedCompanies
            ? [...this.recentlyVisitedCompanies]
            : [],
          watchername: "Recently viewed (up to 30)",
        },
        // watchlistName,
        // mpt,
        // maxHeight,
        // type,
        // mptApplicable,
        max: this.maxLineitems,
        isTickerTypeBg: false,
        maxTickers: this.maxTickers,
        watchlistData: this.watchlistData || [],
        industryData: this.industryMenuData,
        marketGeoData: this.marketGeoData,
        showSaveWatchList: false,
        menuList: [
          {
            id: "recently_visited",
            label: "Recently Visited",
            isLeaf: true,
            dataProperty: "recentlyVisitedMenuData",
            disabled: false,
          },
          {
            id: "industry",
            label: "Industry Peers",
            dataProperty: "industryMenuData",
            isLeaf: false,
          },
          {
            id: "watchlist",
            label: "Watchlists",
            isLeaf: false,
            dataProperty: "watchlistMenuData",
          },
        ],
        addColTableData: [],
        currentIndex: 0,
        marketItemsData: [...MARKET_DATA_LIST].map((item) => ({
          ...item,
          checked: false,
        })),
        isCreateNewModalIndustryMenu: true,
      },
      nzWrapClassName:
        "comparative-analysis-container stepper-wizard-container vertical-center-modal",
      nzFooter: [
        {
          label: "Back",
          type: "default",
          onClick: (data) => {
            data.moveToPreviousStep();
          },
          show: (data) => {
            return data.currentIndex === 1 || data.currentIndex === 2;
          },
        },
        {
          label: "Next",
          type: "primary",
          loading: (data) => data.createLiDataFetch,
          onClick: (data) => {
            data.createLiDataFetch = true;
            data.tickers = data.addRowTableData.map((data) => {
              return data.ticker;
            });
            this.getStdLinesSubscription = this.dataService
              .getStdLineItems(data.tickers, true, "USD", true)
              .subscribe((res) => {
                if (res.data) {
                  this.stdLineItems = [...res.data];
                  data.stdLineItems = [...this.stdLineItems];
                  data.createLiDataFetch = false;
                  data.createSource = res["default_source"];
                  setTimeout(() => data.moveToNextStep(), 200);
                }
              });
            this.getPeriodsWithDataSubscription = this.appService
              .getPeriodsWithData(data.tickers)
              .subscribe((response: { status; dataAvailable }) => {
                if (response.status === "success") {
                  this.periodsWithAvailableDataForPopup =
                    response.dataAvailable;
                  data.periodsWithAvailableDataForPopup =
                    response.dataAvailable;
                }
              });
          },
          show: (data) => {
            return data.currentIndex === 0;
          },
          disabled: (data) => {
            return (
              data.addRowTableData.length < 1 ||
              data.addRowTableData.length > this.maxTickers
            );
          },
        },
        {
          label: "Next",
          type: "primary",
          onClick: (data) => {
            data.moveToNextStep();
          },
          show: (data) => {
            return data.currentIndex === 1;
          },
          disabled: (data) => {
            return (
              data.addColTableData.length < 1 ||
              data.addColTableData.length > data.max
            );
          },
        },
        {
          label: "Save",
          type: "primary",
          onClick: (data) => {
            this.saveNewCompTable(data);
          },
          show: (data) => {
            return data.currentIndex === 2;
          },
          disabled: (data) => {
            return data.tabName === "";
          },
        },
      ],
    });
  }

  saveNewCompTable(data) {
    const payload = createJsonForCreateNewFeature(data);
    this.saveUnsavedCompsSubscription = this.dataService
      .saveUnsavedComps(payload)
      .subscribe((response) => {
        if (response.status === "duplicate_error") {
          data.isDuplicateName = true;
        }
        if (response.status === "success") {
          this.createNewTrendsModal.destroy();
          data.isDuplicateName = false;
          this.router
            .navigate([`/comparative-analysis/user/${response.tab_id}`])
            .then(() => {
              this.dataService.updateLeftPanel(false);
            });
          this.store.dispatch(
            new UpdateComparativeAnalysisData({
              tabId: response.tab_id,
              label: data.tabName,
              operation: "add",
            }),
          );
        }
      });
  }

  /**
   * Makes api call to get lineitems according to tickers.
   * Formats the response object, so that it can be used in `LineItemSearch` component
   */
  fetchAndUpdateStdLineItems(data) {
    const { tickers, showSuggestedLineItems, currency, suggestedLineItems } =
      data;
    if (!tickers || !tickers.length) {
      return;
    }
    this.isStdLineItemsUpdateInProgress = true;
    if (this.lineItemsSubscriber) {
      this.lineItemsSubscriber.unsubscribe();
    }
    this.lineItemsSubscriber = this.dataService
      .getStdLineItems(tickers, showSuggestedLineItems, currency, true)
      .subscribe((response) => {
        if (response.status === "success") {
          const stdData = response.data;
          const formattedStdLineItems = [];
          if (!showSuggestedLineItems && suggestedLineItems) {
            const suggestLineItems = {
              tab_full_name: this.suggestedLineItemMenuName,
              tab_short_name: "SL",
              tab_sli_data: suggestedLineItems,
            };
            formattedStdLineItems.push(suggestLineItems);
          }
          for (const item of stdData) {
            const formattedTabSliData = [];
            // format the tab_sli_data to match data shape of lineItems required for LineitemSearchComponent component
            for (const lineItem of item.tab_sli_data) {
              formattedTabSliData.push({
                ...lineItem,
                label: lineItem.name,
                sli_id_count: lineItem.sli_id_count,
                id: lineItem.uniquesliid,
                disabled: false,
                isLeaf: true,
                level: 2,
                header: false,
                isCollapsed: true,
              });
            }
            const currentItem = {
              tab_full_name: item.tab_full_name,
              tab_short_name: item.tab_short_name,
              tab_sli_data: formattedTabSliData,
            };
            formattedStdLineItems.push(currentItem);
          }
          this.trendCompLineItemsData = formattedStdLineItems;
        }
        this.isStdLineItemsUpdateInProgress = false;
      });
  }

  promptToSave(nextState: RouterStateSnapshot) {
    this.nextStateUrl = nextState.url;
    const details = {
      action_type: "exit_without_saving",
      template_name: this.trendCompState.headerTitle,
      template_id: "",
    };
    this.createActionModal(details);
  }
}
